import * as React from 'react';
import { Icon, Tooltip } from '@patternfly/react-core';
import { ExternalLinkAltIcon } from '@patternfly/react-icons';
import { MultiTypeaheadSelectOption } from '@patternfly/react-templates';
import { Course } from '@app/model/Course';
import { Instructor, allocatableInstructors } from '@app/model/Instructor';
import { SKILL_LEVEL } from '@app/model/Enums';
import {
  CERTIFICATION_STATUS, CertificationCheck, DELIVERABLE_LEVELS, Skill, getCertification,
  getCertificationStatusConfig
} from '@app/model/Skill';
import { ddMMyyyyFormat } from '@app/utils/functions';

/** The course as a reader knows it: its SKU, or its name where a course somehow has no SKU. */
const courseLabel = (course: Course): string => course.sku || course.name || 'this course';

/**
 * A skill level as a reader should see it. Only the four deliverable levels ever reach this, and
 * none of them is an acronym or a compound, so a plain capitalisation is enough - which keeps
 * this off KlassLabels.formatStatusText, a module that imports this one.
 */
const levelText = (level: SKILL_LEVEL): string => level.charAt(0) + level.slice(1).toLowerCase();

/** Why a course with no exam behind it did not qualify the instructor. */
const shortfall = (skill: Skill): string => {
  if (!skill.courseAttendance) {
    return DELIVERABLE_LEVELS.includes(skill.level)
      ? 'the course has not been attended'
      : 'the course has not been attended and no delivery level is recorded';
  }
  return skill.level ? `the recorded level is ${levelText(skill.level)}` : 'no delivery level is recorded';
};

/**
 * The status in a sentence, with what it was decided from, so the icon does not have to be learnt
 * from a legend elsewhere. Every caller shows this - as a tooltip, or as the browser's own title
 * where a PatternFly one cannot go.
 *
 * Two vocabularies, because the two bases answer different questions: an exam is held or has
 * lapsed, while a course with no exam behind it was attended at a level or was not. Saying
 * "not certified" about a course nobody can be certified in would send a reader looking for a
 * certification that does not exist, so the wording says which it is.
 */
const explain = (check: CertificationCheck, course: Course): string => {
  const sku = courseLabel(course);

  // Ahead of the skill row, as the rule is: no examiner, no exam delivery, whatever they hold.
  if (check.blocker === 'NOT_AN_EXAMINER') {
    return `Not an RHCX examiner, so cannot deliver the ${sku} exam.`;
  }

  if (!check.skill) {
    return `No ${sku} skill on record for this instructor.`;
  }

  if (check.basis === 'COURSE') {
    const noExam = `${sku} has no certification exam, so the course record is what counts.`;
    return check.status === CERTIFICATION_STATUS.CERTIFIED
      ? `Skilled to deliver ${sku}: ${levelText(check.skill.level)} level, attended on `
        + `${ddMMyyyyFormat(check.skill.courseAttendance)}. ${noExam}`
      : `Not skilled to deliver ${sku}: ${shortfall(check.skill)}. ${noExam}`;
  }

  switch (check.status) {
    case CERTIFICATION_STATUS.CERTIFIED:
      return `Certified to deliver ${sku}. Certification valid until ${ddMMyyyyFormat(check.skill.expirationDate)}.`;
    case CERTIFICATION_STATUS.EXPIRED:
      return check.skill.expirationDate
        ? `Certification for ${sku} expired on ${ddMMyyyyFormat(check.skill.expirationDate)}.`
        : `Certified for ${sku}, but no expiry date is recorded, so the certification cannot be `
          + `confirmed as current.`;
    default:
      return `Not certified for ${sku}.`;
  }
};

/**
 * The same answer in a few words, for a picker's option rather than a tooltip: one line under the
 * name, in a list that can run to a hundred instructors. The full sentence is `explain`.
 */
export const certificationSummary = (check: CertificationCheck): string => {
  if (check.blocker === 'NOT_AN_EXAMINER') return 'Not an RHCX examiner';
  if (!check.skill) return 'No skill on record';

  if (check.basis === 'COURSE') {
    if (check.status === CERTIFICATION_STATUS.CERTIFIED) {
      return `${levelText(check.skill.level)}, attended ${ddMMyyyyFormat(check.skill.courseAttendance)}`;
    }
    return check.skill.courseAttendance ? `Not skilled - ${shortfall(check.skill)}` : 'Not skilled - never attended';
  }

  switch (check.status) {
    case CERTIFICATION_STATUS.CERTIFIED:
      return `Certified until ${ddMMyyyyFormat(check.skill.expirationDate)}`;
    case CERTIFICATION_STATUS.EXPIRED:
      return check.skill.expirationDate
        ? `Certification expired ${ddMMyyyyFormat(check.skill.expirationDate)}`
        : 'Certified, no expiry date recorded';
    default:
      return 'Not certified';
  }
};

interface CertificationIconProps {
  status: CERTIFICATION_STATUS;
  /**
   * An accessible name for the icon. Pass it where the icon alone carries the meaning; leave it
   * off where neighbouring text or a tooltip already says the same, so a screen reader does not
   * hear it twice.
   */
  label?: string;
}

/**
 * The icon for a certification status. Sized to the text it sits in and coloured from the theme
 * by PatternFly's Icon, so it reads the same in light and dark mode.
 */
export const CertificationIcon: React.FC<CertificationIconProps> = ({ status, label }) => {
  const config = getCertificationStatusConfig(status);
  if (!config) {
    return null;
  }
  const Glyph = config.icon;
  return (
    <Icon status={config.status} isInline {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}>
      <Glyph />
    </Icon>
  );
};

/** The instructor's own page, the one route that shows their skills, dates and allocations. */
const instructorDetailPath = (instructor: Instructor): string => `/instructors/${instructor.id}`;

interface InstructorDetailLinkProps {
  instructor: Instructor;
  /** As on InstructorName, and for the same callers: the browser's own title instead of a
   * PatternFly tooltip. */
  nativeTooltip?: boolean;
}

/**
 * A link to an instructor's own page, opened in a new tab.
 *
 * A new tab rather than a navigation because a name is always met while reading something else - a
 * class, a request, a week of the calendar - and going to look the instructor up should not cost
 * the reader the page they were on, nor the filters and the week they set up to get there. The
 * tooltip says so, since a link that takes the tab away and one that leaves it are the same shape.
 *
 * A plain anchor rather than a router Link: a new tab loads the application afresh either way, so
 * there is nothing for the router to do, and the name stays renderable where no Router is in scope.
 */
export const InstructorDetailLink: React.FC<InstructorDetailLinkProps> = ({ instructor, nativeTooltip }) => {
  // An instructor who reached us as a name with no record behind them has no page to open.
  if (!instructor.id) {
    return null;
  }

  const hint = `Opens ${instructor.name}'s details in a new tab`;
  const link = (
    <a
      href={instructorDetailPath(instructor)}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={hint}
      // Names sit inside click targets of their own - a table row, a calendar chip that opens a
      // popover - and opening the new tab should not also work whatever that click does.
      onClick={event => event.stopPropagation()}
      style={{ marginInlineStart: '0.25rem', whiteSpace: 'nowrap' }}
      {...(nativeTooltip ? { title: hint } : {})}
    >
      {/* The anchor's aria-label is the accessible name, so the glyph itself says nothing. */}
      <Icon isInline aria-hidden><ExternalLinkAltIcon /></Icon>
    </a>
  );

  return nativeTooltip ? link : <Tooltip content={hint}>{link}</Tooltip>;
};

interface InstructorNameProps {
  instructor: Instructor;
  /**
   * The course the instructor is being judged against - a class's course, or a pipeline
   * request's. Without one the name is shown undecorated: certification is a question about a
   * course, and there is nothing to ask when there is no course. See getCertification.
   */
  course?: Course | null;
  /**
   * Use the browser's own title tooltip rather than a PatternFly one. For a caller that is
   * already inside a popover or tooltip trigger - the calendar's class chips - where a nested
   * PatternFly Tooltip fights the outer one for the pointer.
   */
  nativeTooltip?: boolean;
  /**
   * Leave the link to the instructor's page off. For a name rendered inside a click target of its
   * own, where a second thing to click within a line of small text is more hazard than help - the
   * calendar's class chips, which are popover triggers and are a few pixels tall. Those callers
   * show the link in the panel the chip opens instead.
   */
  hideDetailLink?: boolean;
}

/**
 * An instructor's name, followed by whether they may deliver the course in hand and a link to
 * their own page.
 *
 * The one place either decoration is drawn, so a name reads the same in the classes table, on the
 * class and request detail pages and in the calendar. A name with no certification to report - no
 * course, or an instructor whose skills never arrived - is the name and the link alone.
 */
export const InstructorName: React.FC<InstructorNameProps> = ({
  instructor, course, nativeTooltip, hideDetailLink
}) => {
  const certification = getCertification(instructor, course);
  // Outside the name's own tooltip trigger below, deliberately: the two have different things to
  // say, and inside it the certification sentence would answer a hover meant for the link.
  const link = hideDetailLink
    ? null
    : <InstructorDetailLink instructor={instructor} nativeTooltip={nativeTooltip} />;

  if (!certification || !course) {
    return <React.Fragment>{instructor.name}{link}</React.Fragment>;
  }

  // The icon itself stays aria-hidden either way: the wrapper carries the same sentence, as a
  // tooltip or as a title, and both are read out.
  const message = explain(certification, course);
  const content = (
    <React.Fragment>
      {/* Bound to the last word of the name, so the icon is never orphaned onto its own line. */}
      {instructor.name}&nbsp;<CertificationIcon status={certification.status} />
    </React.Fragment>
  );
  const decorated = nativeTooltip
    ? <span title={message}>{content}</span>
    : <Tooltip content={message}><span style={{ cursor: 'help' }}>{content}</span></Tooltip>;

  return <React.Fragment>{decorated}{link}</React.Fragment>;
};


/**
 * An instructor list as a select's options, each carrying whether that instructor may deliver the
 * course in hand.
 *
 * `content` stays the plain name, deliberately: MultiTypeaheadSelect filters on
 * `String(option.content)` and builds its chips from it, so an element there breaks both the
 * typeahead and the selected labels. The status goes in `icon` and `description`, which the
 * template passes straight through to SelectOption - and no Tooltip, which at a hundred options
 * would mean a hundred poppers mounted every time the menu opens. The description says in words
 * what the icon says in colour, so nothing needs hovering.
 */
export const instructorSelectOptions = (
  instructors: Instructor[],
  course?: Course | null
): MultiTypeaheadSelectOption[] =>
  instructors.map(instructor => {
    const check = getCertification(instructor, course);
    return {
      value: instructor.id,
      content: instructor.name,
      ...(check
        ? { icon: <CertificationIcon status={check.status} />, description: certificationSummary(check) }
        : {})
    };
  });

/**
 * The options for an allocation picker, built once per course rather than once per row.
 *
 * The status depends on the class's course, so a table's rows can no longer share one option list
 * the way they did when it was just names. A page shows far fewer distinct courses than rows,
 * though, so they are cached by course and the cache is dropped whenever the instructor list
 * changes. (It is not dropped at midnight, so a page left open overnight can show yesterday's
 * expiry reading in a picker. The cells recompute on every render and do not have that.)
 *
 * `assigned` is who is already on the record: they are appended if the status filter left them
 * out, so editing cannot drop them - see allocatableInstructors.
 */
export const useInstructorOptions = (instructors: Instructor[]) => {
  const cache = React.useMemo(() => new Map<number, MultiTypeaheadSelectOption[]>(), [instructors]);

  return React.useCallback((course?: Course | null, assigned?: Instructor[] | null) => {
    const key = course?.id ?? 0;
    let base = cache.get(key);
    if (!base) {
      base = instructorSelectOptions(allocatableInstructors(instructors), course);
      cache.set(key, base);
    }

    const missing = (assigned ?? []).filter(inst => inst?.id && !base!.some(option => option.value === inst.id));
    // The cached array itself in the ordinary case, so the select is not handed new props on
    // every render of the row it sits in.
    return missing.length ? [...base, ...instructorSelectOptions(missing, course)] : base;
  }, [cache, instructors]);
};
